import { useMemo } from 'react';

import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useCurrentUserAsync } from '#src/app/shared/hooks/session';
import type { StateWithoutError } from '#src/app/shared/utils/async';
import { isLoading } from '#src/app/shared/utils/async';

import { useCaptchaEnabled } from '../../useCaptchaEnabled';
import { usePublicInitialValues } from '../usePublicInitialValues';
import { useSignFormGuestInitialValues } from '../useSignFormGuestInitialValues';
import { useUnauthenticatedSignForm } from '../useUnauthenticatedSignForm';

type InitialValuesState = StateWithoutError<{ values: Partial<FormValues> | undefined }>;

export function useSignFormNonGuestInitialValues(): InitialValuesState {
	const captchaEnabled = useCaptchaEnabled();
	const currentUserState = useCurrentUserAsync();
	const publicInitialValues = usePublicInitialValues();
	const {
		data: { enabled: unauthenticatedSignFormEnabled },
	} = useUnauthenticatedSignForm();
	const guestInitialValues = useSignFormGuestInitialValues();

	// so the loading object is always the same for given params
	const loadingState = useMemo(() => ({ status: 'loading' as const }), []);

	return useMemo(() => {
		if (unauthenticatedSignFormEnabled) {
			return guestInitialValues;
		}

		if (isLoading(currentUserState)) {
			return loadingState;
		}

		if (currentUserState.value) {
			const { address, country, city, state, postalCode, firstName, lastName } = currentUserState.value;

			// weirdly, non-guest users are allowed to sign without a city/postalCode/stateCode/countryCode, while guests cannot...
			return {
				status: 'loaded',
				values: {
					firstName,
					lastName,
					/**
					 * Value used by the `StreetAddressField` component.
					 *
					 * We can't use the `address` prop name since that's already used
					 * to hold the location object when the user inputs their address
					 * using the `AddressField` component.
					 */
					streetAddress: address || '',
					city: city || '',
					postalCode: postalCode || '',
					stateCode: state?.stateCode || '',
					countryCode: country?.countryCode || '',
					geolocPreview: false,
					captchaEnabled,
					...publicInitialValues,
				},
			};
		}

		return {
			status: 'loaded',
			values: undefined,
		};
	}, [
		captchaEnabled,
		currentUserState,
		guestInitialValues,
		loadingState,
		publicInitialValues,
		unauthenticatedSignFormEnabled,
	]);
}
